import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, TextField, FormControl, InputLabel, Select,
    MenuItem, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    Tabs, Tab, Card, CardContent, TextareaAutosize, LinearProgress
} from '@mui/material';
import { Send, Sms, Email, Group, Refresh, History } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const SMSCommunication = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [smsLogs, setSmsLogs] = useState([]);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        type: 'custom',
        recipientType: 'student',
        classId: '',
        sectionId: '',
        message: '',
        templateId: ''
    });

    const [stats, setStats] = useState({
        sent: 0,
        delivered: 0,
        failed: 0,
        credits: 0
    });

    const SMS_TYPES = [
        { value: 'attendance', label: 'Attendance Alert' },
        { value: 'fee', label: 'Fee Reminder' },
        { value: 'exam', label: 'Exam Schedule' },
        { value: 'notice', label: 'General Notice' },
        { value: 'birthday', label: 'Birthday Wishes' },
        { value: 'custom', label: 'Custom Message' }
    ];

    useEffect(() => {
        fetchClasses();
        fetchTemplates();
        fetchSmsLogs();
        fetchStats();
    }, []);

    const fetchClasses = async () => {
        try {
            const res = await api.get('/classes');
            setClasses(res.data.data || []);
        } catch (error) {
            console.error('Failed to fetch classes');
        }
    };

    const fetchTemplates = async () => {
        try {
            const res = await api.get('/sms/templates');
            setTemplates(res.data.data || []);
        } catch (error) {
            // Use default templates
            setTemplates([
                { id: 1, name: 'Attendance Alert', message: 'Dear Parent, Your ward {student_name} was absent today.' },
                { id: 2, name: 'Fee Reminder', message: 'Dear Parent, Kindly pay the pending fee of Rs.{amount} for {student_name}.' },
                { id: 3, name: 'Exam Notice', message: 'Dear Parent, Exams start from {date}. Please prepare {student_name}.' }
            ]);
        }
    };

    const fetchSmsLogs = async () => {
        try {
            const res = await api.get('/sms/logs', { params: { limit: 50 } });
            setSmsLogs(res.data.data || []);
        } catch (error) {
            // Mock data
            setSmsLogs([
                { id: 1, type: 'attendance', message: 'Attendance alert sent', recipientCount: 45, status: 'delivered', sentAt: '2025-01-15 10:30' },
                { id: 2, type: 'fee', message: 'Fee reminder sent', recipientCount: 120, status: 'delivered', sentAt: '2025-01-14 09:00' },
                { id: 3, type: 'notice', message: 'Holiday notice', recipientCount: 500, status: 'delivered', sentAt: '2025-01-13 14:30' }
            ]);
        }
    };

    const fetchStats = async () => {
        try {
            const res = await api.get('/sms/stats');
            setStats(res.data.data || { sent: 1250, delivered: 1200, failed: 50, credits: 5000 });
        } catch (error) {
            setStats({ sent: 1250, delivered: 1200, failed: 50, credits: 5000 });
        }
    };

    const handleClassChange = async (classId) => {
        setFormData({ ...formData, classId, sectionId: '' });
        try {
            const res = await api.get(`/classes/${classId}/sections`);
            setSections(res.data.data || []);
        } catch (error) {
            console.error('Failed to fetch sections');
        }
    };

    const handleTemplateSelect = (templateId) => {
        const template = templates.find(t => t.id === parseInt(templateId));
        if (template) {
            setFormData({ ...formData, templateId, message: template.message });
        }
    };

    const handleSendSms = async () => {
        if (!formData.message.trim()) {
            toast.error('Please enter a message');
            return;
        }
        setLoading(true);
        try {
            await api.post('/sms/send', formData);
            toast.success('SMS sent successfully!');
            fetchSmsLogs();
            fetchStats();
            setFormData({ ...formData, message: '' });
        } catch (error) {
            toast.error('Failed to send SMS');
        } finally {
            setLoading(false);
        }
    };

    const renderComposeTab = () => (
        <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>Compose Message</Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small" margin="normal">
                                <InputLabel>Message Type</InputLabel>
                                <Select
                                    value={formData.type}
                                    label="Message Type"
                                    onChange={e => setFormData({ ...formData, type: e.target.value })}
                                >
                                    {SMS_TYPES.map(t => (
                                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small" margin="normal">
                                <InputLabel>Recipient Type</InputLabel>
                                <Select
                                    value={formData.recipientType}
                                    label="Recipient Type"
                                    onChange={e => setFormData({ ...formData, recipientType: e.target.value })}
                                >
                                    <MenuItem value="student">All Students</MenuItem>
                                    <MenuItem value="parent">All Parents</MenuItem>
                                    <MenuItem value="staff">All Staff</MenuItem>
                                    <MenuItem value="class">By Class</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {formData.recipientType === 'class' && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Class</InputLabel>
                                        <Select
                                            value={formData.classId}
                                            label="Class"
                                            onChange={e => handleClassChange(e.target.value)}
                                        >
                                            {classes.map(c => (
                                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Section</InputLabel>
                                        <Select
                                            value={formData.sectionId}
                                            label="Section"
                                            onChange={e => setFormData({ ...formData, sectionId: e.target.value })}
                                        >
                                            <MenuItem value="">All Sections</MenuItem>
                                            {sections.map(s => (
                                                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <FormControl fullWidth size="small" margin="normal">
                                <InputLabel>Select Template</InputLabel>
                                <Select
                                    value={formData.templateId}
                                    label="Select Template"
                                    onChange={e => handleTemplateSelect(e.target.value)}
                                >
                                    <MenuItem value="">Custom Message</MenuItem>
                                    {templates.map(t => (
                                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Message"
                                value={formData.message}
                                onChange={e => setFormData({ ...formData, message: e.target.value })}
                                placeholder="Enter your message here... Use {student_name}, {amount}, {date} as placeholders"
                                helperText={`Characters: ${formData.message.length}/160 (${Math.ceil(formData.message.length / 160)} SMS)`}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                startIcon={<Send />}
                                onClick={handleSendSms}
                                disabled={loading || !formData.message.trim()}
                                size="large"
                            >
                                {loading ? 'Sending...' : 'Send SMS'}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography color="text.secondary" gutterBottom>SMS Credits</Typography>
                        <Typography variant="h4" color="primary">{stats.credits}</Typography>
                        <LinearProgress
                            variant="determinate"
                            value={(stats.credits / 10000) * 100}
                            sx={{ mt: 1 }}
                        />
                    </CardContent>
                </Card>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography color="text.secondary" gutterBottom>Sent Today</Typography>
                        <Typography variant="h4">{stats.sent}</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography color="text.secondary" gutterBottom>Delivered</Typography>
                        <Typography variant="h4" color="success.main">{stats.delivered}</Typography>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent>
                        <Typography color="text.secondary" gutterBottom>Failed</Typography>
                        <Typography variant="h4" color="error.main">{stats.failed}</Typography>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderHistoryTab = () => (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">SMS History</Typography>
                <Button startIcon={<Refresh />} onClick={fetchSmsLogs}>Refresh</Button>
            </Box>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Date/Time</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Message</TableCell>
                            <TableCell align="right">Recipients</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {smsLogs.map(log => (
                            <TableRow key={log.id}>
                                <TableCell>{log.sentAt}</TableCell>
                                <TableCell><Chip label={log.type} size="small" /></TableCell>
                                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {log.message}
                                </TableCell>
                                <TableCell align="right">{log.recipientCount}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={log.status}
                                        size="small"
                                        color={log.status === 'delivered' ? 'success' : log.status === 'failed' ? 'error' : 'warning'}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

    const renderTemplatesTab = () => (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">SMS Templates</Typography>
                <Button variant="contained">Add Template</Button>
            </Box>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Message</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {templates.map(t => (
                            <TableRow key={t.id}>
                                <TableCell>{t.name}</TableCell>
                                <TableCell>{t.message}</TableCell>
                                <TableCell>
                                    <Button size="small">Edit</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>SMS & Communication</Typography>

            <Paper sx={{ mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                    <Tab label="Compose" icon={<Send />} iconPosition="start" />
                    <Tab label="History" icon={<History />} iconPosition="start" />
                    <Tab label="Templates" icon={<Sms />} iconPosition="start" />
                    <Tab label="Email" icon={<Email />} iconPosition="start" />
                </Tabs>
            </Paper>

            {activeTab === 0 && renderComposeTab()}
            {activeTab === 1 && renderHistoryTab()}
            {activeTab === 2 && renderTemplatesTab()}
            {activeTab === 3 && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6">Email Communication</Typography>
                    <Typography color="text.secondary">Email composition and history will appear here.</Typography>
                </Paper>
            )}
        </Box>
    );
};

export default SMSCommunication;
